import { useCallback, useEffect, useMemo, useState } from 'react';
import { getWeeklyNotesForScopes, WeekNote, WeekNoteScope } from '@features/whereabouts/services/WeekNotesService';
import { Moment } from "moment";
import { DATE_FORMAT } from "@shared/utils/DateUtils";

// Like WithWeeklyNotes, but fetches and combines notes across several weeks at once — used by the
// notes panel in month view to surface every week note in the displayed month.
export function WithWeeklyNotesForDates(scopes: WeekNoteScope[], dates: Moment[]) {
  const [loading, setLoading] = useState(false);
  const [requested, setRequested] = useState(true);
  const [notes, setNotes] = useState<WeekNote[]>([]);

  // dates is a fresh array each render; key on the formatted values so effects only re-run when the
  // actual set of weeks changes.
  const datesKey = useMemo(() => dates.map(d => d.format(DATE_FORMAT)).join(','), [dates]);

  const makeRequest = useCallback(async () => {
    setRequested(true);
    setLoading(true);
    try {
      const resp = await Promise.all(dates.map(async date => {
        const weekCommencing = date.format(DATE_FORMAT);
        const notesForWeek = await getWeeklyNotesForScopes(scopes, date);
        // Tag each note with the week it was queried for so the same person's notes across different
        // weeks stay distinct (notesId is null for day-note-only rows) and can be labelled in the UI.
        return notesForWeek.map(note => ({ ...note, weekCommencing }));
      }));
      setNotes(resp.flat());
    } finally {
      setLoading(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [datesKey, scopes]);

  useEffect(() => {
    if (!scopes || scopes.length === 0 || !dates || dates.length === 0 || loading || requested) return;
    setRequested(true);
    setLoading(true);
    makeRequest();
  }, [datesKey, loading, makeRequest, requested, scopes, dates]);

  useEffect(() => {
    if (scopes && scopes.length > 0 && dates && dates.length > 0) {
      setRequested(false);
    }
  }, [scopes, datesKey, dates]);

  return {
    loading: loading,
    hasRequested: requested,
    notes: notes,
    load: makeRequest,
  }
}
